import { AxiosRequest } from "../ApiCall";
import { configEnv } from "../constant";

export interface SendMessageRequest {
    message: string;
    sessionId?: string;
    organizationId: string;
}

export interface SendMessageResponse {
    success: boolean;
    data: {
        response: string;
        sessionId: string;
        requiresConfirmation?: boolean;
        pendingAction?: {
            type: 'invite_user' | 'schedule_audit' | 'query_data' | 'system_info';
            parameters: any;
            confirmationMessage: string;
        };
        mcpResult?: any;
    };
    message: string;
}

export interface ConfirmActionRequest {
    sessionId: string;
    confirmed: boolean;
    organizationId: string;
}

export interface ConfirmActionResponse {
    success: boolean;
    data: {
        response: string;
        sessionId: string;
        mcpResult?: any;
    };
    message: string;
}

export interface ChatSession {
    _id: string;
    sessionId: string;
    userId: string;
    organizationId: string;
    messages: ChatMessage[];
    status: 'active' | 'completed' | 'failed';
    context: {
        userRole: string[];
        currentOperation?: string;
        pendingActions?: any[];
    };
    mcpOperations: {
        operationType: 'invite_user' | 'query_data' | 'system_info';
        status: 'pending' | 'completed' | 'failed';
        parameters: any;
        result?: any;
        timestamp: string;
    }[];
    createdAt: string;
    updatedAt: string;
}

export interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
    metadata?: {
        mcpOperation?: string;
        functionCalled?: string;
        parameters?: any;
        result?: any;
    };
}

export interface ChatbotStats {
    sse: {
        totalConnections: number;
        connectionsByOrganization: Record<string, number>;
        connectionsByUser: Record<string, number>;
        oldestConnection: number | null;
    };
    database: {
        totalSessions: number;
        activeSessions: number;
        todaySessions: number;
    };
    recentActivity: {
        sessionId: string;
        user: any;
        organization: any;
        messageCount: number;
        lastActivity: string;
        status: string;
    }[];
}

export const chatbotAPI = {
    /**
     * Send a message to the AI chatbot
     */
    sendMessage: async (data: SendMessageRequest): Promise<{ data: SendMessageResponse }> => {
        const access_token = localStorage.getItem("access_token")
            ? JSON.parse(localStorage.getItem("access_token") ?? "")
            : "";
        const header = { Authorization: `Bearer ${access_token}` };

        const response = await AxiosRequest(
            'POST',
            `${configEnv.baseUrl}/chatbot/message`,
            data,
            header
        );
        return response;
    },

    /**
     * Confirm or cancel a pending action in the chatbot
     */
    confirmAction: async (data: ConfirmActionRequest): Promise<{ data: ConfirmActionResponse }> => {
        const access_token = localStorage.getItem("access_token")
            ? JSON.parse(localStorage.getItem("access_token") ?? "")
            : "";
        const header = { Authorization: `Bearer ${access_token}` };

        const response = await AxiosRequest(
            'POST',
            `${configEnv.baseUrl}/chatbot/confirm`,
            data,
            header
        );
        return response;
    },

    /**
     * Get chat history for the current user
     */
    getChatHistory: async (limit?: number, organizationId?: string): Promise<{ data: ChatSession[] }> => {
        const access_token = localStorage.getItem("access_token")
            ? JSON.parse(localStorage.getItem("access_token") ?? "")
            : "";
        const header = { Authorization: `Bearer ${access_token}` };

        const queryParams = new URLSearchParams();
        if (limit) queryParams.append('limit', limit.toString());
        if (organizationId) queryParams.append('organizationId', organizationId);

        const url = `${configEnv.baseUrl}/chatbot/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

        const response = await AxiosRequest('GET', url, undefined, header);
        return response;
    },

    /**
     * Get a specific chat session by ID
     */
    getChatSession: async (sessionId: string, organizationId?: string): Promise<{ data: ChatSession }> => {
        const access_token = localStorage.getItem("access_token")
            ? JSON.parse(localStorage.getItem("access_token") ?? "")
            : "";
        const header = { Authorization: `Bearer ${access_token}` };

        const queryParams = organizationId ? `?organizationId=${organizationId}` : '';
        const url = `${configEnv.baseUrl}/chatbot/session/${sessionId}${queryParams}`;

        const response = await AxiosRequest('GET', url, undefined, header);
        return response;
    },

    /**
     * Get chatbot statistics and health (SuperAdmin only)
     */
    getChatbotStats: async (): Promise<{ data: ChatbotStats }> => {
        const access_token = localStorage.getItem("access_token")
            ? JSON.parse(localStorage.getItem("access_token") ?? "")
            : "";
        const header = { Authorization: `Bearer ${access_token}` };

        const response = await AxiosRequest(
            'GET',
            `${configEnv.baseUrl}/chatbot/stats`,
            undefined,
            header
        );
        return response;
    },

    /**
     * Delete a chat session
     */
    deleteChatSession: async (sessionId: string, organizationId?: string): Promise<{ data: { sessionId: string } }> => {
        const access_token = localStorage.getItem("access_token")
            ? JSON.parse(localStorage.getItem("access_token") ?? "")
            : "";
        const header = { Authorization: `Bearer ${access_token}` };

        const queryParams = organizationId ? `?organizationId=${organizationId}` : '';
        const url = `${configEnv.baseUrl}/chatbot/session/${sessionId}${queryParams}`;

        const response = await AxiosRequest('DELETE', url, undefined, header);
        return response;
    }
}; 